//* Import necessary modules
import React, { useEffect } from "react";

//* Import icons
import { AlertTriangle } from "lucide-react";

interface ModalWarningProps {
  show: boolean;
  msg: string;
  title: string;
  onClose: () => void;
}

/**
 * ModalWarning component displays a warning modal with a message and title.
 * The modal automatically closes after 1.5 seconds if `show` is true.
 *
 * @param {boolean} show - Determines whether the modal is visible.
 * @param {() => void} onClose - Function to call when the modal is closed.
 * @param {string} msg - The message to display inside the modal.
 * @param {string} title - The title to display in the modal header.
 *
 * @returns {JSX.Element | null} The rendered modal component or null if not visible.
 */
export const ModalWarning: React.FC<ModalWarningProps> = ({
  show,
  onClose,
  msg,
  title,
}) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        onClose();
      }, 1500); //

      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  return (
    <>
      {/* Modal */}
      <div
        className="modal fade show"
        tabIndex={-1}
        role="dialog"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            {/* Cabeçalho do modal */}
            <div className="modal-header">
              <h5 className="modal-title d-flex align-items-center">
                <AlertTriangle className="me-2" />
                {title}
              </h5>
              <button type="button" className="close" onClick={onClose} title="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            {/* Corpo do modal */}
            <div className="modal-body">
              <p>{msg}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Fundo escuro do modal */}
      <div className="modal-backdrop fade show"></div>
    </>
  );
};
